import { buildAdminPaymentSlipData } from "../components/Dashboard/AdminDashboard/AdminBookings/buildAdminPaymentSlipData";

function pick(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

function toAmount(...values) {
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function lookupName(list, id, nameKeys = ["name"]) {
  if (id == null || id === "" || !Array.isArray(list)) return "";
  const row = list.find((x) => String(x.id) === String(id));
  if (!row) return "";
  for (const key of nameKeys) {
    if (row[key] != null && String(row[key]).trim() !== "") {
      return String(row[key]);
    }
  }
  return "";
}

/**
 * Map GetParcelProductsForEdit / history rows into PaymentSlip producDetails.
 */
export function mapProductsToSlipDetails(productLines = [], lookups = {}) {
  const {
    itemCategories = [],
    itemTypes = [],
    cargoTypes = [],
  } = lookups;

  return (productLines || []).map((p) => {
    const categoryName =
      pick(
        p.itemCategoryName,
        p.productGroupName,
        lookupName(itemCategories, p.itemCategoryId, [
          "itemCategoryName",
          "categoryName",
          "name",
        ]),
      ) || "-";

    const typeName =
      pick(
        p.itemTypeName,
        p.productTypeName,
        lookupName(itemTypes, p.itemTypeId, [
          "itemTypeName",
          "typeName",
          "name",
        ]),
      ) || "-";

    const customerLength = pick(p.customerParcelLength, p.customerLength);
    const customerWidth = pick(p.customerParcelWidth, p.customerWidth);
    const customerHeight = pick(p.customerParcelHeight, p.customerHeight);
    const customerWeight = pick(p.customerParcelWeight, p.customerWeight);

    const bandLength = pick(p.weightBandLength, p.bandLength);
    const bandWidth = pick(p.weightBandWidth, p.bandWidth);
    const bandHeight = pick(p.weightBandHeight, p.bandHeight);

    // Display dims: weight-band first, else parcel/item fallback (legacy rows).
    const length = pick(bandLength, p.parcelLength, p.itemLength, p.length);
    const width = pick(bandWidth, p.parcelWidth, p.itemWidth, p.width);
    const height = pick(bandHeight, p.parcelHeight, p.itemHeight, p.height);
    const weight = pick(
      p.parcelWeight,
      p.itemWeight,
      p.weight,
      p.weightDescription,
    );

    return {
      productGroupName: categoryName,
      productTypeName: typeName,
      itemDescription: pick(
        p.uniqItemDescription,
        p.itemDescription,
        p.description,
        p.parcelAdditionalInfo,
        "-",
      ),
      productValue: String(
        toAmount(p.itemValue, p.productValue, p.subTotal) || 0,
      ),
      productQty: Math.max(
        1,
        toAmount(p.productQty, p.qty, p.quantity, 1) || 1,
      ),
      preset: pick(p.dimensionName, p.preset, p.weightDescription),
      itemLength: length !== "" ? String(length) : "",
      itemWidth: width !== "" ? String(width) : "",
      itemHeight: height !== "" ? String(height) : "",
      itemWeight: weight !== "" ? String(weight) : "",
      customerLength: customerLength !== "" ? String(customerLength) : "",
      customerWidth: customerWidth !== "" ? String(customerWidth) : "",
      customerHeight: customerHeight !== "" ? String(customerHeight) : "",
      customerWeight: customerWeight !== "" ? String(customerWeight) : "",
      weightBandLength: bandLength !== "" ? String(bandLength) : length !== "" ? String(length) : "",
      weightBandWidth: bandWidth !== "" ? String(bandWidth) : width !== "" ? String(width) : "",
      weightBandHeight: bandHeight !== "" ? String(bandHeight) : height !== "" ? String(height) : "",
      weightDescription: pick(p.weightDescription),
      minimumWeight: p.minimumWeight ?? p.MinimumWeight ?? null,
      maximumWeight: p.maximumWeight ?? p.MaximumWeight ?? null,
      cargoTypeName: pick(
        p.cargoTypeName,
        lookupName(cargoTypes, p.cargoTypeId, ["cargoTypeName", "name"]),
      ),
    };
  });
}

function lineCostTotal(line = {}) {
  const itemValue = toAmount(line.itemValue, line.ItemValue);
  if (itemValue > 0) return itemValue;

  const parts = [
    line.routeCost ?? line.RouteCost,
    line.weightCost ?? line.WeightCost,
    line.itemTypeCost ?? line.ItemTypeCost,
    line.dimensionCost ?? line.DimensionCost,
    line.cargoCost ?? line.CargoCost,
    line.itemCategoryCost ?? line.ItemCategoryCost,
    line.shippingServiceAmount ?? line.ShippingServiceAmount,
    line.extraPackagingCost ?? line.ExtraPackagingCost,
  ].map((v) => toAmount(v));

  const gross = parts.reduce((sum, n) => sum + n, 0);
  const discount = toAmount(line.discountAmount, line.DiscountAmount);
  return Math.max(0, Number((gross - discount).toFixed(2)));
}

function resolveWeightBandDims(line = {}, lookups = {}) {
  const dimensions = Array.isArray(lookups.dimensions) ? lookups.dimensions : [];
  const weights = Array.isArray(lookups.weights) ? lookups.weights : [];

  let dimensionId = line.dimensionId ?? line.DimensionId;
  const weightId = line.weightId ?? line.WeightId;
  const weightRow =
    weightId != null && weightId !== ""
      ? weights.find((w) => String(w.id) === String(weightId))
      : null;

  if (
    (dimensionId == null || dimensionId === "" || Number(dimensionId) === 0) &&
    weightRow?.dimensionId
  ) {
    dimensionId = weightRow.dimensionId;
  }

  const dim =
    dimensionId != null && dimensionId !== ""
      ? dimensions.find((d) => String(d.id) === String(dimensionId))
      : null;

  return {
    length: pick(dim?.length, weightRow?.length),
    width: pick(dim?.width, weightRow?.width),
    height: pick(dim?.height, weightRow?.height),
    dimensionName: pick(
      dim?.dimensionName,
      weightRow?.dimensionName,
      dim?.name,
    ),
  };
}

function normalizePrintLine(line = {}, lookups = {}) {
  const weightDescription = pick(
    line.weightDescription,
    line.WeightDescription,
  );
  const minimumWeight = line.minimumWeight ?? line.MinimumWeight ?? null;
  const maximumWeight = line.maximumWeight ?? line.MaximumWeight ?? null;
  // Snapshot Parcel* L/W/H = customer-given dims; ParcelWeight = remesaure/hub kg.
  const customerLength = pick(
    line.customerParcelLength,
    line.CustomerParcelLength,
    line.parcelLength,
    line.ParcelLength,
  );
  const customerWidth = pick(
    line.customerParcelWidth,
    line.CustomerParcelWidth,
    line.parcelWidth,
    line.ParcelWidth,
  );
  const customerHeight = pick(
    line.customerParcelHeight,
    line.CustomerParcelHeight,
    line.parcelHeight,
    line.ParcelHeight,
  );
  const hubWeight = pick(line.parcelWeight, line.ParcelWeight);
  const customerWeight = pick(
    line.customerParcelWeight,
    line.CustomerParcelWeight,
  );
  const hubWeightLabel =
    hubWeight !== ""
      ? String(hubWeight)
      : weightDescription ||
        (minimumWeight != null && maximumWeight != null
          ? `${minimumWeight}-${maximumWeight}`
          : "");
  const customerWeightLabel =
    customerWeight !== "" ? String(customerWeight) : "";

  const band = resolveWeightBandDims(line, lookups);
  const dimensionName = pick(band.dimensionName, weightDescription);

  return {
    itemCategoryId: line.itemCategoryId ?? line.ItemCategoryId,
    itemTypeId: line.itemTypeId ?? line.ItemTypeId,
    itemCategoryName: pick(
      line.itemCategoryName,
      line.ItemCategoryName,
      line.productGroupName,
    ),
    itemTypeName: pick(
      line.itemTypeName,
      line.ItemTypeName,
      line.productTypeName,
    ),
    cargoTypeId: line.cargoTypeId ?? line.CargoTypeId,
    cargoTypeName: pick(line.cargoTypeName, line.CargoTypeName),
    weightId: line.weightId ?? line.WeightId,
    dimensionId: line.dimensionId ?? line.DimensionId,
    uniqItemDescription: pick(
      line.uniqItemDescription,
      line.UniqItemDescription,
    ),
    parcelAdditionalInfo: pick(
      line.parcelAdditionalInfo,
      line.ParcelAdditionalInfo,
    ),
    itemValue: lineCostTotal(line),
    productQty: Math.max(
      1,
      toAmount(line.productQty, line.ProductQty, line.itemQty, 1) || 1,
    ),
    customerParcelLength: customerLength,
    customerParcelWidth: customerWidth,
    customerParcelHeight: customerHeight,
    customerParcelWeight: customerWeightLabel,
    weightBandLength: band.length,
    weightBandWidth: band.width,
    weightBandHeight: band.height,
    // parcel* = weight-band capacity for slip "pricing dims"
    parcelLength: band.length !== "" ? band.length : customerLength,
    parcelWidth: band.width !== "" ? band.width : customerWidth,
    parcelHeight: band.height !== "" ? band.height : customerHeight,
    parcelWeight: hubWeightLabel,
    weightDescription,
    minimumWeight,
    maximumWeight,
    dimensionName,
    discountAmount: toAmount(line.discountAmount, line.DiscountAmount),
    discountPercentage: toAmount(
      line.discountPercentage,
      line.DiscountPercentage,
    ),
    pickUpCost: toAmount(line.pickUpCost, line.PickUpCost),
    pickupCost: toAmount(line.pickUpCost, line.PickUpCost),
    routeName: "",
  };
}

/**
 * Map GetInvoiceForPrint.lines into product rows for the slip builder.
 * Does not use live GetParcelProductsForEdit.
 * Resolves weight-band LÃ—WÃ—H from DimensionId / WeightId + lookups.
 */
export function mapPrintLinesToProductLines(lines = [], lookups = {}) {
  return (Array.isArray(lines) ? lines : []).map((line) =>
    normalizePrintLine(line, lookups),
  );
}

function buildPrintVersionRemeasurement(productLines = [], note = "") {
  const first = productLines[0];
  if (!first) return null;

  const customerInput = {
    length: pick(first.customerParcelLength, first.customerLength),
    width: pick(first.customerParcelWidth, first.customerWidth),
    height: pick(first.customerParcelHeight, first.customerHeight),
    weight: pick(first.customerParcelWeight, first.customerWeight),
  };
  const hubMeasurement = {
    length: pick(first.weightBandLength, first.parcelLength),
    width: pick(first.weightBandWidth, first.parcelWidth),
    height: pick(first.weightBandHeight, first.parcelHeight),
    // Hub / remesaure kg (pricing weight) - not customer weight
    weight: pick(first.parcelWeight, first.itemWeight),
  };

  const hasCustomer =
    customerInput.length !== "" ||
    customerInput.width !== "" ||
    customerInput.height !== "" ||
    customerInput.weight !== "";
  const hasBand =
    hubMeasurement.length !== "" ||
    hubMeasurement.width !== "" ||
    hubMeasurement.height !== "" ||
    hubMeasurement.weight !== "";

  if (!hasCustomer && !hasBand) return null;

  return {
    customerInput: hasCustomer ? customerInput : null,
    hubMeasurement: hasBand ? hubMeasurement : null,
    note: note || "",
    customerLabel: "Customer given",
    hubLabel: "Weight band",
  };
}

function kgLabel(value) {
  if (value == null || String(value).trim() === "") return "";
  const s = String(value).trim().replace(/\s*kg$/i, "");
  return s ? `${s}kg` : "";
}

export function measurementParts(m) {
  if (!m) return { length: "", width: "", height: "", dims: "", weight: "" };
  const length = m.length ?? m.itemLength ?? m.parcelLength ?? "";
  const width = m.width ?? m.itemWidth ?? m.parcelWidth ?? "";
  const height = m.height ?? m.itemHeight ?? m.parcelHeight ?? "";
  const hasDims =
    String(length).trim() !== "" &&
    String(width).trim() !== "" &&
    String(height).trim() !== "";
  return {
    length,
    width,
    height,
    dims: hasDims ? `${length} × ${width} × ${height} in` : "",
    weight: kgLabel(m.weight ?? m.itemWeight ?? m.parcelWeight),
  };
}

export function formatMeasurementLine(m) {
  const parts = measurementParts(m);
  return [parts.dims, parts.weight].filter(Boolean).join(" · ");
}

export function describeMeasurementChange(from, to) {
  if (!to) return "";
  const parts = [];
  const fw = String(from?.weight ?? "")
    .replace(/\s*kg$/i, "")
    .trim();
  const tw = String(to?.weight ?? "")
    .replace(/\s*kg$/i, "")
    .trim();
  if (fw && tw && fw !== tw) parts.push(`Weight ${fw}kg → ${tw}kg`);
  else if (!fw && tw) parts.push(`Weight set to ${tw}kg`);

  const fromDims = [from?.length, from?.width, from?.height]
    .filter((v) => v != null && String(v).trim() !== "")
    .join("×");
  const toDims = [to?.length, to?.width, to?.height]
    .filter((v) => v != null && String(v).trim() !== "")
    .join("×");
  if (fromDims && toDims && fromDims !== toDims) {
    parts.push(`Dims ${fromDims} → ${toDims} in`);
  } else if (!fromDims && toDims) {
    parts.push(`Dims ${toDims} in`);
  }
  return parts.join(" · ");
}

function applyCustomerMeta(remeasurement, parcelId) {
  if (!remeasurement || parcelId == null) return remeasurement;
  const meta = loadRemeasurementMeta(parcelId);
  if (!meta?.customerInput) return remeasurement;
  const hubW = remeasurement.hubMeasurement?.weight;
  const cust = remeasurement.customerInput || {};
  const metaWeight = meta.customerInput.weight;
  const customerLooksLikeHub =
    cust.weight === "" ||
    cust.weight == null ||
    (hubW != null && String(cust.weight) === String(hubW));
  const nextCustomer = {
    length: cust.length || meta.customerInput.length || "",
    width: cust.width || meta.customerInput.width || "",
    height: cust.height || meta.customerInput.height || "",
    weight:
      customerLooksLikeHub &&
      metaWeight != null &&
      String(metaWeight).trim() !== ""
        ? String(metaWeight)
        : cust.weight || String(metaWeight || ""),
  };
  const hasCustomer =
    nextCustomer.length !== "" ||
    nextCustomer.width !== "" ||
    nextCustomer.height !== "" ||
    nextCustomer.weight !== "";
  if (!hasCustomer) return remeasurement;
  return {
    ...remeasurement,
    customerInput: nextCustomer,
  };
}

/**
 * Customer-given vs hub/charged measurements from GetInvoiceForPrint (or live product lines).
 */
export function extractPrintMeasurements(
  printPayload,
  lookups = {},
  parcelId = null,
) {
  if (!printPayload || typeof printPayload !== "object") return null;
  const lines = Array.isArray(printPayload.lines)
    ? printPayload.lines
    : Array.isArray(printPayload.Lines)
      ? printPayload.Lines
      : Array.isArray(printPayload)
        ? printPayload
        : [];
  if (!lines.length) return null;

  const productLines = mapPrintLinesToProductLines(lines, lookups);
  const first = productLines[0] || {};
  let remeasurement = buildPrintVersionRemeasurement(productLines);
  const pid =
    parcelId ??
    printPayload.parcelId ??
    printPayload.ParcelId ??
    first.parcelId ??
    null;
  remeasurement = applyCustomerMeta(remeasurement, pid);
  if (!remeasurement && !first.weightDescription && !first.dimensionName) {
    return null;
  }

  const bandName = pick(
    first.weightDescription,
    first.minimumWeight != null && first.maximumWeight != null
      ? `${first.minimumWeight}-${first.maximumWeight} kg`
      : "",
  );

  return {
    customerInput: remeasurement?.customerInput || null,
    hubMeasurement: remeasurement?.hubMeasurement || null,
    weightBandName: bandName,
    dimensionName: pick(first.dimensionName),
  };
}

/**
 * Overlay live product fields onto print lines when snapshot omitted them
 * (customer measurements, item category / type ids).
 */
export function mergeCustomerMeasurementsOntoPrintLines(
  printLines = [],
  liveProducts = [],
) {
  const live = Array.isArray(liveProducts) ? liveProducts : [];
  return (Array.isArray(printLines) ? printLines : []).map((line, idx) => {
    const liveRow = live[idx] || live[0] || {};
    const customerWeight = pick(
      line.customerParcelWeight,
      line.CustomerParcelWeight,
      liveRow.customerParcelWeight,
      liveRow.CustomerParcelWeight,
    );
    const customerLength = pick(
      line.customerParcelLength,
      line.CustomerParcelLength,
      liveRow.customerParcelLength,
      liveRow.CustomerParcelLength,
    );
    const customerWidth = pick(
      line.customerParcelWidth,
      line.CustomerParcelWidth,
      liveRow.customerParcelWidth,
      liveRow.CustomerParcelWidth,
    );
    const customerHeight = pick(
      line.customerParcelHeight,
      line.CustomerParcelHeight,
      liveRow.customerParcelHeight,
      liveRow.CustomerParcelHeight,
    );

    const itemCategoryId =
      line.itemCategoryId ??
      line.ItemCategoryId ??
      liveRow.itemCategoryId ??
      liveRow.ItemCategoryId;
    const itemTypeId =
      line.itemTypeId ??
      line.ItemTypeId ??
      liveRow.itemTypeId ??
      liveRow.ItemTypeId;
    const itemCategoryName = pick(
      line.itemCategoryName,
      line.ItemCategoryName,
      liveRow.itemCategoryName,
      liveRow.ItemCategoryName,
    );
    const itemTypeName = pick(
      line.itemTypeName,
      line.ItemTypeName,
      liveRow.itemTypeName,
      liveRow.ItemTypeName,
    );

    const next = { ...line };

    if (customerWeight) {
      next.customerParcelWeight = customerWeight;
      next.CustomerParcelWeight = customerWeight;
    }
    if (customerLength) {
      next.customerParcelLength = customerLength;
      next.CustomerParcelLength = customerLength;
    }
    if (customerWidth) {
      next.customerParcelWidth = customerWidth;
      next.CustomerParcelWidth = customerWidth;
    }
    if (customerHeight) {
      next.customerParcelHeight = customerHeight;
      next.CustomerParcelHeight = customerHeight;
    }
    if (itemCategoryId != null && itemCategoryId !== "") {
      next.itemCategoryId = itemCategoryId;
      next.ItemCategoryId = itemCategoryId;
    }
    if (itemTypeId != null && itemTypeId !== "") {
      next.itemTypeId = itemTypeId;
      next.ItemTypeId = itemTypeId;
    }
    if (itemCategoryName) {
      next.itemCategoryName = itemCategoryName;
      next.ItemCategoryName = itemCategoryName;
    }
    if (itemTypeName) {
      next.itemTypeName = itemTypeName;
      next.ItemTypeName = itemTypeName;
    }

    return next;
  });
}

/**
 * Build PaymentSlip data from GetInvoiceForPrint (historical line snapshots).
 * Amount comes from printPayload.orderAmount. Product dims/weight from lines.
 */
export function buildInvoiceSlipFromPrintPayload({
  printPayload = null,
  parcelRow = {},
  costData = {},
  invoiceRow = {},
  invoiceHistory = [],
  lookups = {},
  liveProducts = [],
} = {}) {
  if (!printPayload) return null;

  const rawLines = Array.isArray(printPayload.lines) ? printPayload.lines : [];
  const sourceLines =
    rawLines.length > 0
      ? rawLines
      : Array.isArray(liveProducts) && liveProducts.length > 0
        ? liveProducts
        : [];
  const lines = mergeCustomerMeasurementsOntoPrintLines(
    sourceLines,
    liveProducts,
  );
  const productLines = mapPrintLinesToProductLines(lines, lookups);
  const usedLiveFallback = Boolean(
    printPayload.usedLiveFallback ||
      (rawLines.length === 0 && sourceLines.length > 0),
  );

  const orderAmount = toAmount(
    printPayload.orderAmount,
    invoiceRow.orderAmount,
  );

  const discountTotal = productLines.reduce(
    (sum, p) => sum + toAmount(p.discountAmount),
    0,
  );
  const pickupTotal = productLines.reduce(
    (sum, p) => sum + toAmount(p.pickUpCost, p.pickupCost),
    0,
  );
  const discountPct = toAmount(
    productLines[0]?.discountPercentage,
    printPayload.discountPercentage,
  );

  const amendmentDescription = pick(
    printPayload.description,
    printPayload.Description,
    invoiceRow.description,
  );

  const adjustmentTypeRaw = pick(
    printPayload.adjustmentType,
    printPayload.AdjustmentType,
    invoiceRow.adjustmentType,
    invoiceRow.AdjustmentType,
    "None",
  );
  const adjustmentTypeNorm = String(adjustmentTypeRaw || "None").toLowerCase();
  // Real amendment only - do not treat original booking bank-transfer notes as amendments
  const isRealAmendment =
    adjustmentTypeNorm === "due" ||
    adjustmentTypeNorm === "credit" ||
    adjustmentTypeNorm === "amended";

  const enrichedInvoice = {
    ...invoiceRow,
    id: printPayload.paymentId ?? invoiceRow.id,
    paymentId: printPayload.paymentId ?? invoiceRow.paymentId,
    paymentInvoiceNo: pick(
      printPayload.paymentInvoiceNo,
      printPayload.PaymentInvoiceNo,
      invoiceRow.paymentInvoiceNo,
      invoiceRow.PaymentInvoiceNo,
    ),
    orderAmount,
    paymentStatusId:
      printPayload.paymentStatusId ?? invoiceRow.paymentStatusId,
    statusName: pick(printPayload.statusName, invoiceRow.statusName),
    adjustmentType: adjustmentTypeRaw,
    parentPaymentId:
      printPayload.parentPaymentId ?? invoiceRow.parentPaymentId,
    description: amendmentDescription,
    currencyType: pick(
      printPayload.currencyType,
      invoiceRow.currencyType,
      costData.currencyType,
      "GBP",
    ),
    currencyTypeId:
      printPayload.currencyTypeId ??
      invoiceRow.currencyTypeId ??
      costData.currencyTypeId,
    symbol: pick(
      printPayload.currencySymbol,
      invoiceRow.symbol,
      invoiceRow.currencySymbol,
      costData.symbol,
    ),
    creationDate: pick(printPayload.creationDate, invoiceRow.creationDate),
  };

  const enrichedParcel = {
    ...parcelRow,
    parcelId: printPayload.parcelId ?? parcelRow.parcelId,
    parcelNo: printPayload.parcelId ?? parcelRow.parcelId ?? parcelRow.parcelNo,
    recordSerialNo: pick(
      printPayload.recordSerialNo,
      parcelRow.recordSerialNo,
    ),
    barcode: pick(printPayload.barcode, parcelRow.barcode),
    senderName: pick(
      printPayload.senderName,
      parcelRow.senderName,
      costData.senderName,
    ),
    senderEmail: pick(
      printPayload.senderEmail,
      parcelRow.senderEmail,
      costData.senderEmail,
    ),
    senderPhone: pick(
      printPayload.senderPhone,
      parcelRow.senderPhone,
      costData.senderPhone,
    ),
    senderPostCode: pick(
      printPayload.senderPostCode,
      printPayload.SenderPostCode,
      parcelRow.senderPostCode,
      costData.senderPostCode,
    ),
    senderDoorNo: pick(
      printPayload.senderDoorNo,
      printPayload.SenderDoorNo,
      parcelRow.senderDoorNo,
      costData.senderDoorNo,
      costData.SenderDoorNo,
    ),
    senderStreetName: pick(
      printPayload.senderStreetName,
      printPayload.SenderStreetName,
      parcelRow.senderStreetName,
      costData.senderStreetName,
      costData.SenderStreetName,
    ),
    senderAdditionalAddressInfo: pick(
      printPayload.senderAdditionalAddressInfo,
      printPayload.SenderAdditionalAddressInfo,
      costData.senderAdditionalAddressInfo,
      costData.SenderAdditionalAddressInfo,
      parcelRow.senderAdditionalAddressInfo,
    ),
    senderCityId: pick(
      printPayload.senderCityId,
      printPayload.SenderCityId,
      costData.senderCityId,
      costData.SenderCityId,
      parcelRow.senderCityId,
    ),
    senderCountryId: pick(
      printPayload.senderCountryId,
      printPayload.SenderCountryId,
      costData.senderCountryId,
      costData.SenderCountryId,
      parcelRow.senderCountryId,
    ),
    senderAddress: pick(
      printPayload.senderAdditionalAddressInfo,
      printPayload.SenderAdditionalAddressInfo,
      costData.senderAdditionalAddressInfo,
      costData.SenderAdditionalAddressInfo,
      parcelRow.senderAdditionalAddressInfo,
      parcelRow.senderAddress,
    ),
    receiverName: pick(
      printPayload.receiverName,
      parcelRow.receiverName,
      costData.receiverName,
    ),
    receiverEmail: pick(
      printPayload.receiverEmail,
      parcelRow.receiverEmail,
      costData.receiverEmail,
    ),
    receiverPhone: pick(
      printPayload.receiverPhone,
      parcelRow.receiverPhone,
      costData.receiverPhone,
    ),
    receiverPostCode: pick(
      printPayload.receiverPostCode,
      printPayload.ReceiverPostCode,
      parcelRow.receiverPostCode,
      costData.receiverPostCode,
    ),
    receiverDoorNo: pick(
      printPayload.receiverDoorNo,
      printPayload.ReceiverDoorNo,
      parcelRow.receiverDoorNo,
      costData.receiverDoorNo,
      costData.ReceiverDoorNo,
    ),
    receiverStreetName: pick(
      printPayload.receiverStreetName,
      printPayload.ReceiverStreetName,
      parcelRow.receiverStreetName,
      costData.receiverStreetName,
      costData.ReceiverStreetName,
    ),
    receiverAdditionalAddressInfo: pick(
      printPayload.receiverAdditionalAddressInfo,
      printPayload.ReceiverAdditionalAddressInfo,
      costData.receiverAdditionalAddressInfo,
      costData.ReceiverAdditionalAddressInfo,
      parcelRow.receiverAdditionalAddressInfo,
    ),
    receiverCityId: pick(
      printPayload.receiverCityId,
      printPayload.ReceiverCityId,
      costData.receiverCityId,
      costData.ReceiverCityId,
      parcelRow.receiverCityId,
    ),
    receiverCountryId: pick(
      printPayload.receiverCountryId,
      printPayload.ReceiverCountryId,
      costData.receiverCountryId,
      costData.ReceiverCountryId,
      parcelRow.receiverCountryId,
    ),
    receiverAddress: pick(
      printPayload.receiverAdditionalAddressInfo,
      printPayload.ReceiverAdditionalAddressInfo,
      costData.receiverAdditionalAddressInfo,
      costData.ReceiverAdditionalAddressInfo,
      parcelRow.receiverAdditionalAddressInfo,
      parcelRow.receiverAddress,
    ),
  };

  const enrichedCost = {
    ...costData,
    currencyType: enrichedInvoice.currencyType,
    currencyTypeId: enrichedInvoice.currencyTypeId,
    symbol: enrichedInvoice.symbol,
    recordSerialNo: enrichedParcel.recordSerialNo,
    barcode: enrichedParcel.barcode,
    senderName: enrichedParcel.senderName,
    senderEmail: enrichedParcel.senderEmail,
    senderMobileNo: enrichedParcel.senderPhone,
    senderPostCode: enrichedParcel.senderPostCode,
    senderDoorNo: enrichedParcel.senderDoorNo,
    senderStreetName: enrichedParcel.senderStreetName,
    senderAdditionalAddressInfo: enrichedParcel.senderAdditionalAddressInfo,
    senderCityId: enrichedParcel.senderCityId,
    senderCountryId: enrichedParcel.senderCountryId,
    receiverName: enrichedParcel.receiverName,
    receiverEmail: enrichedParcel.receiverEmail,
    receiverMobileNo: enrichedParcel.receiverPhone,
    receiverPostCode: enrichedParcel.receiverPostCode,
    receiverDoorNo: enrichedParcel.receiverDoorNo,
    receiverStreetName: enrichedParcel.receiverStreetName,
    receiverAdditionalAddressInfo:
      enrichedParcel.receiverAdditionalAddressInfo,
    receiverCityId: enrichedParcel.receiverCityId,
    receiverCountryId: enrichedParcel.receiverCountryId,
    // Header amount is authoritative for this invoice version
    subTotal: orderAmount,
    orderPayableAmount: orderAmount,
    discountAmount: discountTotal,
    discountPercentage: discountPct,
    pickupCost: pickupTotal,
    pickUpCost: pickupTotal,
    vaT_TaxAmount: usedLiveFallback
      ? toAmount(printPayload.currentParcelVatAmount)
      : 0,
    paymentTypeMethodId:
      printPayload.paymentMethodId ?? costData.paymentTypeMethodId,
  };

  // Pass print lines straight through - do not resolve against live products /
  // localStorage remesaure snapshots (that would mix V2 hub weight into V1).
  const slip = buildInvoiceVersionSlipData({
    parcelRow: enrichedParcel,
    costData: enrichedCost,
    invoiceRow: enrichedInvoice,
    paymentDetail: {
      paymentInvoiceNo: enrichedInvoice.paymentInvoiceNo,
      orderAmount,
      currencyType: enrichedInvoice.currencyType,
      currencyTypeId: enrichedInvoice.currencyTypeId,
      symbol: enrichedInvoice.symbol,
      paymentStatusId: enrichedInvoice.paymentStatusId,
      statusName: enrichedInvoice.statusName,
      paymentTypeMethodId: printPayload.paymentMethodId,
      senderName: enrichedParcel.senderName,
      senderEmail: enrichedParcel.senderEmail,
      senderPhone: enrichedParcel.senderPhone,
      senderPostCode: enrichedParcel.senderPostCode,
      senderDoorNo: enrichedParcel.senderDoorNo,
      senderStreetName: enrichedParcel.senderStreetName,
      senderAdditionalAddressInfo: enrichedParcel.senderAdditionalAddressInfo,
      senderCityId: enrichedParcel.senderCityId,
      senderCountryId: enrichedParcel.senderCountryId,
      receiverName: enrichedParcel.receiverName,
      receiverEmail: enrichedParcel.receiverEmail,
      receiverPhone: enrichedParcel.receiverPhone,
      receiverPostCode: enrichedParcel.receiverPostCode,
      receiverDoorNo: enrichedParcel.receiverDoorNo,
      receiverStreetName: enrichedParcel.receiverStreetName,
      receiverAdditionalAddressInfo:
        enrichedParcel.receiverAdditionalAddressInfo,
      receiverCityId: enrichedParcel.receiverCityId,
      receiverCountryId: enrichedParcel.receiverCountryId,
      recordSerialNo: enrichedParcel.recordSerialNo,
      barcode: enrichedParcel.barcode,
      discountAmount: discountTotal,
      pickupCost: pickupTotal,
      pickUpCost: pickupTotal,
    },
    productLines,
    lookups,
    invoiceHistory,
    historyDetails: productLines,
    skipProductLineResolve: true,
    skipRemeasurement: true,
  });

  if (!slip) return null;

  const parentPaymentId = enrichedInvoice.parentPaymentId;
  const parentRow =
    parentPaymentId != null
      ? (Array.isArray(invoiceHistory) ? invoiceHistory : []).find(
          (r) =>
            Number(r.id ?? r.paymentId) === Number(parentPaymentId) ||
            Number(r.paymentId) === Number(parentPaymentId),
        )
      : null;

  let remeasurement = buildPrintVersionRemeasurement(
    productLines,
    amendmentDescription,
  );

  // If customer weight was never stored separately (or was overwritten by remesaure),
  // prefer durable remesaure-meta customerInput.weight from Amend save.
  const parcelIdForMeta =
    printPayload.parcelId ?? parcelRow.parcelId ?? invoiceRow.parcelId;
  if (remeasurement?.customerInput && parcelIdForMeta != null) {
    const meta = loadRemeasurementMeta(parcelIdForMeta);
    const metaWeight = meta?.customerInput?.weight;
    const hubW = remeasurement.hubMeasurement?.weight;
    const custW = remeasurement.customerInput.weight;
    if (
      metaWeight != null &&
      String(metaWeight).trim() !== "" &&
      (custW === "" ||
        custW == null ||
        (hubW != null && String(custW) === String(hubW)))
    ) {
      remeasurement = {
        ...remeasurement,
        customerInput: {
          ...remeasurement.customerInput,
          weight: String(metaWeight),
        },
      };
    }
  }

  // Keep line-item customerWeight in sync with remesaurement
  let producDetails = slip.producDetails;
  if (
    Array.isArray(producDetails) &&
    remeasurement?.customerInput?.weight != null &&
    String(remeasurement.customerInput.weight).trim() !== ""
  ) {
    producDetails = producDetails.map((row, idx) =>
      idx === 0
        ? {
            ...row,
            customerWeight: String(remeasurement.customerInput.weight),
            customerLength:
              row.customerLength ||
              String(remeasurement.customerInput.length || ""),
            customerWidth:
              row.customerWidth ||
              String(remeasurement.customerInput.width || ""),
            customerHeight:
              row.customerHeight ||
              String(remeasurement.customerInput.height || ""),
          }
        : row,
    );
  }

  const adjustmentType = pick(enrichedInvoice.adjustmentType, "None");
  const showAmendment = isRealAmendment;

  return {
    ...slip,
    ...(producDetails ? { producDetails } : {}),
    paymentInvoiceNo: pick(
      enrichedInvoice.paymentInvoiceNo,
      printPayload.paymentInvoiceNo,
      printPayload.PaymentInvoiceNo,
      invoiceRow.paymentInvoiceNo,
      invoiceRow.PaymentInvoiceNo,
      slip.paymentInvoiceNo,
    ),
    paymentTypeId: String(
      printPayload.paymentMethodId || slip.paymentTypeId || "5",
    ),
    paymentMethodName: pick(printPayload.paymentMethodName),
    orderAmount,
    usedLiveFallback,
    hasSnapshot: Boolean(printPayload.hasSnapshot),
    // Always attach version dims (customer given + weight band) for print path
    remeasurement: remeasurement || slip.remeasurement || null,
    amendmentDetails: showAmendment
      ? {
          adjustmentType,
          description: amendmentDescription,
          parentPaymentId: parentPaymentId ?? null,
          parentInvoiceNo: pick(parentRow?.paymentInvoiceNo),
          version: invoiceRow.version ?? invoiceRow.Version ?? null,
          orderAmount,
          paymentInvoiceNo: pick(
            enrichedInvoice.paymentInvoiceNo,
            printPayload.paymentInvoiceNo,
            printPayload.PaymentInvoiceNo,
          ),
        }
      : null,
    versionMoney: {
      ...(slip.versionMoney || {}),
      subtotal: orderAmount,
      discount: discountTotal,
      vat: usedLiveFallback
        ? toAmount(printPayload.currentParcelVatAmount)
        : toAmount(slip.versionMoney?.vat),
      pickup: pickupTotal,
      total: orderAmount,
    },
  };
}

/**
 * Build PaymentSlip override data for a specific invoice version.
 * Uses parcel list row + cost/details APIs + optional GetPaymentByInvoiceNo.
 * Product lines / remesaure / totals are scoped to the selected version.
 */
export function buildInvoiceVersionSlipData({
  parcelRow = {},
  costData = {},
  invoiceRow = {},
  paymentDetail = null,
  productLines = [],
  lookups = {},
  invoiceHistory = [],
  originalMeasurement = null,
  historyDetails = [],
  skipProductLineResolve = false,
  skipRemeasurement = false,
} = {}) {
  const parcelId =
    parcelRow.parcelId ??
    paymentDetail?.parcelId ??
    paymentDetail?.parcelNo ??
    invoiceRow.parcelId;

  const versionProductLines = skipProductLineResolve
    ? Array.isArray(productLines)
      ? productLines
      : []
    : resolveProductLinesForInvoiceVersion(
        invoiceRow,
        productLines,
        parcelId,
        historyDetails,
        paymentDetail,
      );

  const amount = toAmount(
    invoiceRow.orderAmount,
    paymentDetail?.orderAmount,
    // Do not fall back to live parcel cost for historical versions -
    // that mixes remesaure totals into V1.
    isRemeasureDueInvoice(invoiceRow)
      ? costData.orderPayableAmount
      : null,
    isRemeasureDueInvoice(invoiceRow) ? costData.subTotal : null,
  );

  const currencyType = pick(
    invoiceRow.currencyType,
    invoiceRow.currencyCode,
    paymentDetail?.currencyType,
    costData.currencyType,
    "GBP",
  );
  const symbol = pick(
    invoiceRow.symbol,
    invoiceRow.currencySymbol,
    paymentDetail?.symbol,
    paymentDetail?.currencySymbol,
    costData.symbol,
    currencyType,
  );

  const slipProducts = mapProductsToSlipDetails(versionProductLines, lookups);

  const detailsForBuilder =
    Array.isArray(versionProductLines) && versionProductLines.length > 0
      ? versionProductLines.map((p, idx) => {
          const slip = slipProducts[idx] || {};
          return {
            subTotal:
              toAmount(p.itemValue, p.subTotal, slip.productValue) || amount,
            currencyType,
            symbol,
            uniqItemDescription: slip.itemDescription,
            parcelAdditionalInfo: p.parcelAdditionalInfo || "",
            dimensionName: slip.preset,
            weightDescription: slip.itemWeight,
            itemCategoryName: slip.productGroupName,
            itemTypeName: slip.productTypeName,
            cargoTypeName: slip.cargoTypeName || slip.productGroupName,
            shippingServiceName: slip.productTypeName,
            routeName: p.routeName || "",
            itemValue: toAmount(p.itemValue, p.productValue),
            productQty: slip.productQty,
            parcelLength: slip.itemLength,
            parcelWidth: slip.itemWidth,
            parcelHeight: slip.itemHeight,
            parcelWeight: slip.itemWeight,
          };
        })
      : [
          {
            subTotal: amount,
            currencyType,
            symbol,
            uniqItemDescription: "Parcel booking",
            weightDescription: "",
            dimensionName: "",
            itemValue: 0,
            productQty: 1,
          },
        ];

  const firstProduct = slipProducts[0] || {};
  const firstLine = versionProductLines[0] || {};

  const parcel = {
    parcelNo: parcelId,
    senderName: pick(
      parcelRow.senderName,
      paymentDetail?.senderName,
      costData.senderName,
    ),
    senderEmail: pick(
      parcelRow.senderEmail,
      paymentDetail?.senderEmail,
      costData.senderEmail,
    ),
    senderPhone: pick(
      parcelRow.senderPhone,
      paymentDetail?.senderPhone,
      paymentDetail?.senderMobileNo,
      costData.senderMobileNo,
    ),
    senderPostCode: pick(
      parcelRow.senderPostCode,
      paymentDetail?.senderPostCode,
      costData.senderPostCode,
    ),
    senderAddress: pick(
      costData.senderAdditionalAddressInfo,
      costData.SenderAdditionalAddressInfo,
      paymentDetail?.senderAdditionalAddressInfo,
      paymentDetail?.SenderAdditionalAddressInfo,
      parcelRow.senderAdditionalAddressInfo,
      // Avoid history `senderAddress` when it is only a door number - prefer
      // additional-address fields first (matches customer booking slip).
      parcelRow.senderAddress,
    ),
    senderDoorNo: pick(
      costData.senderDoorNo,
      costData.SenderDoorNo,
      paymentDetail?.senderDoorNo,
      parcelRow.senderDoorNo,
    ),
    senderStreetName: pick(
      costData.senderStreetName,
      costData.SenderStreetName,
      paymentDetail?.senderStreetName,
      parcelRow.senderStreetName,
    ),
    senderCityId: pick(
      costData.senderCityId,
      costData.SenderCityId,
      paymentDetail?.senderCityId,
      parcelRow.senderCityId,
    ),
    senderCountryId: pick(
      costData.senderCountryId,
      costData.SenderCountryId,
      paymentDetail?.senderCountryId,
      parcelRow.senderCountryId,
    ),
    receiverName: pick(
      paymentDetail?.receiverName,
      costData.receiverName,
      parcelRow.receiverName,
    ),
    receiverEmail: pick(
      paymentDetail?.receiverEmail,
      costData.receiverEmail,
      parcelRow.receiverEmail,
    ),
    receiverPhone: pick(
      paymentDetail?.receiverPhone,
      paymentDetail?.receiverMobileNo,
      costData.receiverMobileNo,
      costData.receiverPhone,
      parcelRow.receiverPhone,
    ),
    receiverPostCode: pick(
      paymentDetail?.receiverPostCode,
      costData.receiverPostCode,
      parcelRow.receiverPostCode,
    ),
    receiverAddress: pick(
      costData.receiverAdditionalAddressInfo,
      costData.ReceiverAdditionalAddressInfo,
      paymentDetail?.receiverAdditionalAddressInfo,
      paymentDetail?.ReceiverAdditionalAddressInfo,
      parcelRow.receiverAdditionalAddressInfo,
      paymentDetail?.receiverAddress,
      costData.receiverAddress,
      parcelRow.receiverAddress,
    ),
    receiverDoorNo: pick(
      costData.receiverDoorNo,
      costData.ReceiverDoorNo,
      paymentDetail?.receiverDoorNo,
      parcelRow.receiverDoorNo,
    ),
    receiverStreetName: pick(
      costData.receiverStreetName,
      costData.ReceiverStreetName,
      paymentDetail?.receiverStreetName,
      parcelRow.receiverStreetName,
    ),
    receiverCityId: pick(
      paymentDetail?.receiverCityId,
      costData.receiverCityId,
      costData.ReceiverCityId,
      parcelRow.receiverCityId,
    ),
    receiverCity: pick(
      paymentDetail?.receiverCity,
      costData.receiverCity,
      parcelRow.receiverCity,
    ),
    receiverCountryId: pick(
      paymentDetail?.receiverCountryId,
      costData.receiverCountryId,
      costData.ReceiverCountryId,
      parcelRow.receiverCountryId,
    ),
    receiverCountry: pick(
      paymentDetail?.receiverCountry,
      costData.receiverCountry,
      parcelRow.receiverCountry,
    ),
    details: detailsForBuilder,
  };

  const paymentBreakdown = buildPaymentBreakdown(invoiceRow, invoiceHistory);

  // Remesaure comparison only on Due / Amended versions - not on Original V1.
  // Skip when lines already come from GetInvoiceForPrint snapshots.
  const remeasurement =
    !skipRemeasurement && isRemeasureDueInvoice(invoiceRow)
      ? buildRemeasurement({
          productLines: versionProductLines.length
            ? versionProductLines
            : productLines,
          paymentDetail,
          costData,
          originalMeasurement,
          paymentBreakdown,
          parcelId,
          historyDetails,
        })
      : null;

  const displayAmount =
    paymentBreakdown?.subtotal > 0 ? paymentBreakdown.subtotal : amount;

  // Historical Original/Superseded: money from THIS invoice, not live remesaure cost
  const versionScopedCost = isRemeasureDueInvoice(invoiceRow)
    ? costData
    : {
        ...costData,
        subTotal: displayAmount,
        orderPayableAmount: displayAmount,
        discountAmount: toAmount(
          paymentDetail?.discountAmount,
          invoiceRow.discountAmount,
          0,
        ),
        discountPercentage: toAmount(
          paymentDetail?.discountPercentage,
          invoiceRow.discountPercentage,
          0,
        ),
        vaT_TaxAmount: toAmount(
          paymentDetail?.vaT_TaxAmount,
          paymentDetail?.vatAmount,
          invoiceRow.vaT_TaxAmount,
          0,
        ),
        pickupCost: toAmount(
          paymentDetail?.pickupCost,
          paymentDetail?.pickUpCost,
          invoiceRow.pickupCost,
          0,
        ),
        pickUpCost: toAmount(
          paymentDetail?.pickUpCost,
          paymentDetail?.pickupCost,
          invoiceRow.pickupCost,
          0,
        ),
      };

  const slip = buildAdminPaymentSlipData(parcel, {
    ...versionScopedCost,
    subTotal: displayAmount,
    orderPayableAmount: displayAmount,
    recordSerialNo: pick(
      parcelRow.recordSerialNo,
      paymentDetail?.recordSerialNo,
      costData.recordSerialNo,
    ),
    barcode: pick(parcelRow.barcode, paymentDetail?.barcode, costData.barcode),
    currencyType,
    currencyTypeId:
      invoiceRow.currencyTypeId ??
      paymentDetail?.currencyTypeId ??
      costData.currencyTypeId,
    symbol,
    senderAdditionalAddressInfo: pick(
      costData.senderAdditionalAddressInfo,
      costData.SenderAdditionalAddressInfo,
      paymentDetail?.senderAdditionalAddressInfo,
      paymentDetail?.SenderAdditionalAddressInfo,
      parcelRow.senderAdditionalAddressInfo,
      parcel.senderAddress,
    ),
    senderDoorNo: pick(
      costData.senderDoorNo,
      costData.SenderDoorNo,
      paymentDetail?.senderDoorNo,
      parcel.senderDoorNo,
    ),
    senderStreetName: pick(
      costData.senderStreetName,
      costData.SenderStreetName,
      paymentDetail?.senderStreetName,
      parcel.senderStreetName,
    ),
    senderCityId: pick(
      costData.senderCityId,
      costData.SenderCityId,
      paymentDetail?.senderCityId,
      parcel.senderCityId,
    ),
    senderCountryId: pick(
      costData.senderCountryId,
      costData.SenderCountryId,
      paymentDetail?.senderCountryId,
      parcel.senderCountryId,
    ),
    receiverAdditionalAddressInfo: pick(
      costData.receiverAdditionalAddressInfo,
      costData.ReceiverAdditionalAddressInfo,
      paymentDetail?.receiverAdditionalAddressInfo,
      paymentDetail?.ReceiverAdditionalAddressInfo,
      parcelRow.receiverAdditionalAddressInfo,
      parcel.receiverAddress,
    ),
    receiverDoorNo: pick(
      costData.receiverDoorNo,
      costData.ReceiverDoorNo,
      paymentDetail?.receiverDoorNo,
      parcel.receiverDoorNo,
    ),
    receiverStreetName: pick(
      costData.receiverStreetName,
      costData.ReceiverStreetName,
      paymentDetail?.receiverStreetName,
      parcel.receiverStreetName,
    ),
    receiverCityId: pick(
      costData.receiverCityId,
      costData.ReceiverCityId,
      paymentDetail?.receiverCityId,
      parcelRow.receiverCityId,
    ),
    receiverCountryId: pick(
      costData.receiverCountryId,
      costData.ReceiverCountryId,
      paymentDetail?.receiverCountryId,
      parcelRow.receiverCountryId,
    ),
  });

  const producDetails =
    slipProducts.length > 0
      ? slipProducts
      : [
          {
            productGroupName: "-",
            productTypeName: "-",
            itemDescription: "Parcel booking",
            productValue: "0",
            productQty: 1,
            preset: "",
            itemLength: "",
            itemWidth: "",
            itemHeight: "",
            itemWeight: "",
          },
        ];

  return {
    ...slip,
    paymentInvoiceNo: pick(
      invoiceRow.paymentInvoiceNo,
      paymentDetail?.paymentInvoiceNo,
      paymentDetail?.invoiceNumber,
    ),
    receiverName: pick(
      slip.receiverName,
      parcel.receiverName,
      costData.receiverName,
      paymentDetail?.receiverName,
    ),
    receiverEmail: pick(
      slip.receiverEmail,
      parcel.receiverEmail,
      costData.receiverEmail,
      paymentDetail?.receiverEmail,
    ),
    receiverMobileNumber: pick(
      slip.receiverMobileNumber,
      parcel.receiverPhone,
      costData.receiverMobileNo,
      costData.receiverPhone,
      paymentDetail?.receiverMobileNo,
      paymentDetail?.receiverPhone,
    ),
    // Full street line (door + street + additional) + city/country IDs for PaymentSlip
    senderAddress: pick(
      slip.senderAddress,
      costData.senderAdditionalAddressInfo,
      costData.SenderAdditionalAddressInfo,
      paymentDetail?.senderAdditionalAddressInfo,
      parcel.senderAddress,
    ),
    senderPostCode: pick(
      slip.senderPostCode,
      paymentDetail?.senderPostCode,
      costData.senderPostCode,
      parcel.senderPostCode,
      parcelRow.senderPostCode,
    ),
    senderCity: pick(
      slip.senderCity,
      costData.senderCityId,
      costData.SenderCityId,
      paymentDetail?.senderCityId,
      paymentDetail?.SenderCityId,
      parcel.senderCityId,
      parcelRow.senderCityId,
      costData.senderCity,
      parcel.senderCity,
    ),
    senderCountry: pick(
      slip.senderCountry,
      costData.senderCountryId,
      costData.SenderCountryId,
      paymentDetail?.senderCountryId,
      paymentDetail?.SenderCountryId,
      parcel.senderCountryId,
      parcelRow.senderCountryId,
      costData.senderCountry,
      parcel.senderCountry,
    ),
    receiverAddress: pick(
      slip.receiverAddress,
      costData.receiverAdditionalAddressInfo,
      costData.ReceiverAdditionalAddressInfo,
      paymentDetail?.receiverAdditionalAddressInfo,
      parcel.receiverAddress,
    ),
    receiverPostCode: pick(
      slip.receiverPostCode,
      paymentDetail?.receiverPostCode,
      costData.receiverPostCode,
      parcel.receiverPostCode,
      parcelRow.receiverPostCode,
    ),
    receiverCity: pick(
      slip.receiverCity,
      costData.receiverCityId,
      costData.ReceiverCityId,
      paymentDetail?.receiverCityId,
      paymentDetail?.ReceiverCityId,
      parcel.receiverCityId,
      parcelRow.receiverCityId,
      costData.receiverCity,
      parcel.receiverCity,
    ),
    receiverCountry: pick(
      slip.receiverCountry,
      costData.receiverCountryId,
      costData.ReceiverCountryId,
      paymentDetail?.receiverCountryId,
      paymentDetail?.ReceiverCountryId,
      parcel.receiverCountryId,
      parcelRow.receiverCountryId,
      costData.receiverCountry,
      parcel.receiverCountry,
    ),
    productLength: firstProduct.itemLength || "",
    productWidth: firstProduct.itemWidth || "",
    productHeight: firstProduct.itemHeight || "",
    productWeight: firstProduct.itemWeight || "",
    preset: firstProduct.preset || firstLine.dimensionName || "",
    producDetails,
    paymentBreakdown,
    remeasurement,
    // Expose version-scoped money for the Invoice Manager cards
    versionMoney: {
      subtotal: displayAmount,
      discount: toAmount(versionScopedCost.discountAmount),
      vat: toAmount(versionScopedCost.vaT_TaxAmount),
      pickup: toAmount(
        versionScopedCost.pickupCost,
        versionScopedCost.pickUpCost,
      ),
      total: toAmount(invoiceRow.orderAmount, amount),
      paid: toAmount(paymentBreakdown?.paid),
      due: toAmount(paymentBreakdown?.due),
    },
    transitCost: [
      {
        totalCost: displayAmount,
        totalCostWithoutVAT: Math.max(
          0,
          displayAmount - toAmount(versionScopedCost.vaT_TaxAmount),
        ),
        totalCostWithVAT: displayAmount,
        vatPercentageAmount: toAmount(versionScopedCost.vaT_TaxAmount),
        disCountedPercentageAmount: Number(versionScopedCost.discountAmount) || 0,
        disCountedPercentage:
          Number(versionScopedCost.discountPercentage) || 0,
        afterDiscountCost: Math.max(
          0,
          displayAmount - (Number(versionScopedCost.discountAmount) || 0),
        ),
      },
    ],
  };
}

function paymentIdOf(row) {
  return row?.id ?? row?.paymentId ?? null;
}

function isPaidInvoice(row) {
  if (!row) return false;
  if (Number(row.paymentStatusId) === 2) return true;
  return String(row.statusName || "")
    .toLowerCase()
    .includes("paid");
}

/**
 * Subtotal / Paid / Due for the *selected* invoice version only.
 * V1 Original/Paid â†’ Paid = amount, Due = 0 (do not merge later Due invoices).
 * V2 Due â†’ Paid = parent amount, Due = this invoice amount.
 */
export function buildPaymentBreakdown(invoiceRow = {}, invoiceHistory = []) {
  const history = Array.isArray(invoiceHistory) ? invoiceHistory : [];
  const currentAmount = toAmount(invoiceRow.orderAmount);
  const adj = String(invoiceRow.adjustmentType || "").toLowerCase();

  const parent =
    history.find(
      (r) => Number(paymentIdOf(r)) === Number(invoiceRow.parentPaymentId),
    ) ||
    history.find(
      (r) => String(r.adjustmentType || "").toLowerCase() === "superseded",
    ) ||
    null;

  // Due adjustment invoice (extra charge after remesaure)
  if (adj === "due") {
    const paid = toAmount(
      parent?.orderAmount,
      invoiceRow.previousPayableAmount,
    );
    const due = currentAmount;
    return {
      subtotal: Number((paid + due).toFixed(2)),
      paid,
      due,
      showBreakdown: true,
    };
  }

  if (adj === "credit") {
    const paid = toAmount(parent?.orderAmount, currentAmount);
    return {
      subtotal: currentAmount || paid,
      paid,
      due: 0,
      showBreakdown: true,
    };
  }

  if (adj === "amended" && parent) {
    const paid = toAmount(
      parent?.orderAmount,
      invoiceRow.previousPayableAmount,
    );
    const subtotal =
      currentAmount ||
      Number((paid + toAmount(invoiceRow.adjustmentAmount)).toFixed(2));
    const due = Math.max(0, Number((subtotal - paid).toFixed(2)));
    return { subtotal, paid, due, showBreakdown: due > 0 || paid > 0 };
  }

  // Original / None / Superseded / plain paid invoice - show THIS version only
  if (isPaidInvoice(invoiceRow)) {
    return {
      subtotal: currentAmount,
      paid: currentAmount,
      due: 0,
      showBreakdown: true,
    };
  }

  return {
    subtotal: currentAmount,
    paid: 0,
    due: currentAmount,
    showBreakdown: true,
  };
}

export function isRemeasureDueInvoice(invoiceRow) {
  const adj = String(invoiceRow?.adjustmentType || "").toLowerCase();
  return adj === "due" || adj === "amended";
}

/** Product lines appropriate for the selected invoice version. */
export function resolveProductLinesForInvoiceVersion(
  invoiceRow,
  currentProductLines = [],
  parcelId,
  historyDetails = [],
  paymentDetail = null,
) {
  // Due / amended â†’ current hub measurements from API
  if (isRemeasureDueInvoice(invoiceRow)) {
    return Array.isArray(currentProductLines) ? currentProductLines : [];
  }

  // Original / superseded â†’ customer booking snapshot if we have it
  const ids = [parcelId, invoiceRow?.parcelId].filter(
    (id) => id != null && id !== "",
  );
  for (const id of ids) {
    const meta = loadRemeasurementMeta(id);
    if (meta?.customerInput) {
      return [
        {
          ...((currentProductLines && currentProductLines[0]) || {}),
          ...(Array.isArray(historyDetails) && historyDetails[0]
            ? historyDetails[0]
            : {}),
          parcelLength: meta.customerInput.length,
          parcelWidth: meta.customerInput.width,
          parcelHeight: meta.customerInput.height,
          parcelWeight: meta.customerInput.weight,
          itemLength: meta.customerInput.length,
          itemWidth: meta.customerInput.width,
          itemHeight: meta.customerInput.height,
          itemWeight: meta.customerInput.weight,
          uniqItemDescription:
            (currentProductLines &&
              currentProductLines[0]?.uniqItemDescription) ||
            (historyDetails && historyDetails[0]?.uniqItemDescription) ||
            "",
        },
      ];
    }
    try {
      const raw =
        localStorage.getItem(`parcelRemeasureOriginal_${id}`) ||
        sessionStorage.getItem(`parcelRemeasureOriginal_${id}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const rows = Array.isArray(parsed) ? parsed : [parsed];
      if (rows.length) {
        return rows.map((p, idx) => ({
          ...((currentProductLines && currentProductLines[idx]) || {}),
          ...(Array.isArray(historyDetails) && historyDetails[idx]
            ? historyDetails[idx]
            : {}),
          ...p,
          parcelLength: p.parcelLength || p.itemLength,
          parcelWidth: p.parcelWidth || p.itemWidth,
          parcelHeight: p.parcelHeight || p.itemHeight,
          parcelWeight: p.parcelWeight || p.itemWeight,
        }));
      }
    } catch {
      /* ignore */
    }
  }

  // Prefer GetParcelHistory details for original versions (often still booking-time)
  if (Array.isArray(historyDetails) && historyDetails.length) {
    const hub = currentProductLines?.[0];
    const hist = historyDetails[0];
    const hubW = pick(hub?.parcelWeight, hub?.itemWeight);
    const histW = pick(hist?.parcelWeight, hist?.itemWeight, hist?.weight);
    // Use history when it differs from hub (still has customer values)
    if (histW && (!hubW || String(histW) !== String(hubW))) {
      return historyDetails.map((h, idx) => ({
        ...((currentProductLines && currentProductLines[idx]) || {}),
        ...h,
        parcelLength: pick(h.parcelLength, h.itemLength, h.length),
        parcelWidth: pick(h.parcelWidth, h.itemWidth, h.width),
        parcelHeight: pick(h.parcelHeight, h.itemHeight, h.height),
        parcelWeight: pick(h.parcelWeight, h.itemWeight, h.weight),
      }));
    }
  }

  // Payment record for this invoice version
  if (paymentDetail) {
    const fromPayment = measurementFromProduct(paymentDetail);
    if (fromPayment) {
      return [
        {
          ...((currentProductLines && currentProductLines[0]) || {}),
          parcelLength: fromPayment.length,
          parcelWidth: fromPayment.width,
          parcelHeight: fromPayment.height,
          parcelWeight: fromPayment.weight,
          itemLength: fromPayment.length,
          itemWidth: fromPayment.width,
          itemHeight: fromPayment.height,
          itemWeight: fromPayment.weight,
        },
      ];
    }
  }

  // Fallback: paymentSlipData for this parcel + matching invoice (if known)
  try {
    const raw = localStorage.getItem("paymentSlipData");
    if (raw) {
      const slip = JSON.parse(raw);
      const slipId = slip?.parcelId ?? slip?.parcelNo;
      const parcelMatches = ids.some((id) => String(id) === String(slipId));
      const invNo = invoiceRow?.paymentInvoiceNo;
      const invoiceMatches =
        !invNo ||
        !slip?.paymentInvoiceNo ||
        String(slip.paymentInvoiceNo) === String(invNo);
      if (parcelMatches && invoiceMatches) {
        const details = Array.isArray(slip.producDetails)
          ? slip.producDetails
          : [];
        if (details.length) {
          return details.map((item) => ({
            ...item,
            parcelLength: item.itemLength || item.parcelLength,
            parcelWidth: item.itemWidth || item.parcelWidth,
            parcelHeight: item.itemHeight || item.parcelHeight,
            parcelWeight: item.itemWeight || item.parcelWeight,
            uniqItemDescription:
              item.itemDescription || item.uniqItemDescription,
            itemCategoryName: item.productGroupName,
            itemTypeName: item.productTypeName,
          }));
        }
      }
    }
  } catch {
    /* ignore */
  }

  return Array.isArray(currentProductLines) ? currentProductLines : [];
}

function measurementFromProduct(p) {
  if (!p) return null;
  const length = pick(p.parcelLength, p.itemLength, p.length, p.originalParcelLength);
  const width = pick(p.parcelWidth, p.itemWidth, p.width, p.originalParcelWidth);
  const height = pick(p.parcelHeight, p.itemHeight, p.height, p.originalParcelHeight);
  const weight = pick(p.parcelWeight, p.itemWeight, p.weight, p.originalParcelWeight);
  if (!length && !width && !height && !weight) return null;
  return { length, width, height, weight };
}

function measurementFromOriginalFields(p) {
  if (!p) return null;
  const length = pick(
    p.originalParcelLength,
    p.customerParcelLength,
    p.previousParcelLength,
    p.originalLength,
  );
  const width = pick(
    p.originalParcelWidth,
    p.customerParcelWidth,
    p.previousParcelWidth,
    p.originalWidth,
  );
  const height = pick(
    p.originalParcelHeight,
    p.customerParcelHeight,
    p.previousParcelHeight,
    p.originalHeight,
  );
  const weight = pick(
    p.originalParcelWeight,
    p.customerParcelWeight,
    p.previousParcelWeight,
    p.originalWeight,
  );
  if (!length && !width && !height && !weight) return null;
  return { length, width, height, weight };
}

export function buildRemeasurement({
  productLines = [],
  paymentDetail = null,
  costData = {},
  originalMeasurement = null,
  paymentBreakdown = null,
  parcelId = null,
  historyDetails = [],
} = {}) {
  const first = productLines[0] || {};
  const hubMeasurement =
    measurementFromProduct(first) ||
    measurementFromProduct(paymentDetail) ||
    measurementFromProduct(
      Array.isArray(historyDetails) ? historyDetails[0] : null,
    ) ||
    null;

  const ids = [
    parcelId,
    first.parcelId,
    paymentDetail?.parcelId,
    paymentDetail?.parcelNo,
    costData?.parcelId,
    costData?.parcelNo,
  ].filter((id) => id != null && id !== "");

  // 1) Durable remesaure meta (written on Amend save)
  let customerInput = originalMeasurement || null;
  if (!customerInput) {
    for (const id of ids) {
      const meta = loadRemeasurementMeta(id);
      if (meta?.customerInput) {
        customerInput = meta.customerInput;
        break;
      }
    }
  }

  // 2) Explicit original* fields on API rows
  if (!customerInput) {
    customerInput =
      measurementFromOriginalFields(first) ||
      measurementFromOriginalFields(paymentDetail) ||
      measurementFromOriginalFields(costData) ||
      null;
  }

  // 3) Snapshot from booking create / first Amend
  if (!customerInput && typeof localStorage !== "undefined") {
    try {
      for (const id of ids) {
        const raw =
          localStorage.getItem(`parcelRemeasureOriginal_${id}`) ||
          sessionStorage.getItem(`parcelRemeasureOriginal_${id}`);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        customerInput = Array.isArray(parsed)
          ? measurementFromProduct(parsed[0]) ||
            measurementFromBookingItem(parsed[0])
          : measurementFromProduct(parsed) ||
            measurementFromBookingItem(parsed);
        if (customerInput) break;
      }
    } catch {
      /* ignore */
    }
  }

  // 4) Last paymentSlipData if it matches this parcel
  if (!customerInput && typeof localStorage !== "undefined") {
    try {
      const raw = localStorage.getItem("paymentSlipData");
      if (raw) {
        const slip = JSON.parse(raw);
        const slipParcelId = slip?.parcelId ?? slip?.parcelNo;
        const matches = ids.some((id) => String(id) === String(slipParcelId));
        if (matches) {
          const item = Array.isArray(slip.producDetails)
            ? slip.producDetails[0]
            : null;
          customerInput =
            measurementFromBookingItem(item) ||
            measurementFromProduct({
              parcelLength: slip.productLength,
              parcelWidth: slip.productWidth,
              parcelHeight: slip.productHeight,
              parcelWeight: slip.productWeight,
            });
        }
      }
    } catch {
      /* ignore */
    }
  }

  // 5) History detail row (may still hold booked L/W/H/weight on some APIs)
  if (!customerInput && Array.isArray(historyDetails) && historyDetails.length) {
    customerInput =
      measurementFromProduct(historyDetails[0]) ||
      measurementFromBookingItem(historyDetails[0]);
    // If history was already overwritten with hub values, ignore when equal to hub
    if (
      customerInput &&
      hubMeasurement &&
      String(customerInput.weight) === String(hubMeasurement.weight) &&
      String(customerInput.length) === String(hubMeasurement.length)
    ) {
      customerInput = null;
    }
  }

  const hasDue = Number(paymentBreakdown?.due) > 0;
  if (!hubMeasurement && !customerInput) return null;
  if (!hasDue && !customerInput) return null;

  return {
    customerInput,
    hubMeasurement,
    note: hasDue
      ? "Hub remesaure changed weight/dimensions; extra amount due."
      : undefined,
  };
}

function measurementFromBookingItem(item) {
  if (!item) return null;
  const length = pick(
    item.itemLength,
    item.parcelLength,
    item.productLength,
    item.length,
  );
  const width = pick(
    item.itemWidth,
    item.parcelWidth,
    item.productWidth,
    item.width,
  );
  const height = pick(
    item.itemHeight,
    item.parcelHeight,
    item.productHeight,
    item.height,
  );
  const weight = pick(
    item.itemWeight,
    item.parcelWeight,
    item.productWeight,
    item.weight,
  );
  if (!length && !width && !height && !weight) return null;
  return { length, width, height, weight };
}

export function loadRemeasurementMeta(parcelId) {
  if (parcelId == null || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(`parcelRemeasureMeta_${parcelId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveRemeasurementMeta(parcelId, meta = {}) {
  if (parcelId == null || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      `parcelRemeasureMeta_${parcelId}`,
      JSON.stringify({ ...meta, savedAt: new Date().toISOString() }),
    );
  } catch {
    /* ignore */
  }
}

export function saveOriginalMeasurementSnapshot(parcelId, products = []) {
  if (parcelId == null) return;
  try {
    const snapshot = (products || []).map((p) => ({
      parcelLength: pick(p.parcelLength, p.itemLength, p.productLength),
      parcelWidth: pick(p.parcelWidth, p.itemWidth, p.productWidth),
      parcelHeight: pick(p.parcelHeight, p.itemHeight, p.productHeight),
      parcelWeight: pick(p.parcelWeight, p.itemWeight, p.productWeight),
      itemLength: pick(p.parcelLength, p.itemLength, p.productLength),
      itemWidth: pick(p.parcelWidth, p.itemWidth, p.productWidth),
      itemHeight: pick(p.parcelHeight, p.itemHeight, p.productHeight),
      itemWeight: pick(p.parcelWeight, p.itemWeight, p.productWeight),
    }));
    const raw = JSON.stringify(snapshot);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(`parcelRemeasureOriginal_${parcelId}`, raw);
    }
    if (typeof localStorage !== "undefined") {
      // Never overwrite an existing original once captured
      if (!localStorage.getItem(`parcelRemeasureOriginal_${parcelId}`)) {
        localStorage.setItem(`parcelRemeasureOriginal_${parcelId}`, raw);
      }
    }
  } catch {
    /* ignore */
  }
}
